import React from "react";
import { Button, PageError } from "@lumika/ui";

/**
 * Root error boundary — last line of defense for the whole workspace.
 *
 * WHY: no boundary existed anywhere, so a single throwing component (e.g. a
 * provider-shaped activity payload crashing one bubble render) white-screened
 * the entire app. Rendering the shared PageError with a Reload action keeps
 * the failure visible and recoverable instead.
 */
type RootErrorBoundaryState = { hasError: boolean };

export class RootErrorBoundary extends React.Component<
  React.PropsWithChildren,
  RootErrorBoundaryState
> {
  override state: RootErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[root-error-boundary]", error, info.componentStack);
  }

  override render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <PageError
          title="Something went wrong"
          description="The app hit an unexpected error. Reload to pick up where you left off."
          action={<Button onClick={() => window.location.reload()}>Reload</Button>}
        />
      );
    }
    return this.props.children;
  }
}
